import React, { useEffect, useState } from 'react';
import { branch, compose, defaultProps, renderComponent } from 'recompose';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import withLifecycle from '@hocs/with-lifecycle';
import { Loader, SpacerVertical, Typography } from '@klarna/bubble-ui';
import { components as clientComponents } from 'mage-client';
import { MOET_CONSTANTS } from 'moet-core';

import capabilities from '@/selectors/capabilities';
import { getCountries } from '@/selectors/options';
import { CAPS } from '@/constants';

import { FlowRenderer } from './FlowRenderer';
import { getLocationQueryParam } from './selectors';
import { FlowOptionsMenu } from './FlowOptionsMenu';

const { WIDGET_TYPE } = MOET_CONSTANTS;
const { region: regionFromEnv = 'eu' } = window.config?.common?.environment || {};

const styles = {
  loaderWrapper: {
    alignItems: 'center',
    display: 'flex',
    flex: 1,
    justifyContent: 'center',
  },
  fieldSetWrapper: { zIndex: 100 },
  graphWrapper: { zIndex: 0, flex: 1, display: 'flex', borderTop: '1px solid rgb(240, 238, 235)' },
};

const FlowVisualizer = ({ countries = [], country, region, showExcludedSteps, widgetType }) => {
  const history = useHistory();

  const [options, setOptions] = useState({ country, region, showExcludedSteps, widgetType });

  useEffect(() => {
    const urlParams = new URLSearchParams();

    for (const key in options) {
      if (Object.hasOwnProperty.call(options, key)) {
        const value = options[key];

        if (value) {
          urlParams.append(key, value);
        } else {
          urlParams.delete(key);
        }
      }
    }

    // Set url query param
    history.push({ search: urlParams.toString() });
  }, [options, history]);

  return (
    <>
      <Typography textToken="text-style/headings/titles/bold/tertiary">
        Onboarding flow visualizer
      </Typography>
      <div style={styles.fieldSetWrapper}>
        <FlowOptionsMenu value={options} countries={countries} onChange={setOptions} />
      </div>
      <SpacerVertical extraSmall />
      <div style={styles.graphWrapper}>
        <FlowRenderer
          country={options.country}
          region={options.region}
          widgetType={options.widgetType}
          showExcludedSteps={options.showExcludedSteps}
        />
      </div>
    </>
  );
};

FlowVisualizer.propTypes = {
  country: PropTypes.string.isRequired,
  region: PropTypes.string.isRequired,
  showExcludedSteps: PropTypes.bool.isRequired,
  widgetType: PropTypes.string.isRequired,
  countries: PropTypes.array,
};

const INIT_CAPS = {
  getOptions: CAPS.V2_OPTIONS_GET,
};

export const MoetFlowVisualizer = compose(
  clientComponents.withPromiseBasedCapabilities(INIT_CAPS),
  connect(state => ({
    capsFinished: capabilities.hasFinished(INIT_CAPS)(state),
    countries: getCountries(state),
    country: getLocationQueryParam('country')(state),
    region: getLocationQueryParam('region')(state),
    showExcludedSteps: (state => getLocationQueryParam('showExcludedSteps')(state) === 'true')(
      state,
    ),
    widgetType: getLocationQueryParam('widgetType')(state),
  })),
  defaultProps({
    country: 'SE',
    region: regionFromEnv.toLowerCase() || 'eu',
    widgetType: WIDGET_TYPE.UNMANAGED_KYC,
  }),
  withLifecycle({
    async onWillMount({ getOptions }) {
      await getOptions();
    },
  }),
  branch(
    ({ capsFinished }) => !capsFinished,
    renderComponent(() => (
      <div style={styles.loaderWrapper}>
        <Loader />
      </div>
    )),
  ),
)(FlowVisualizer);
